import React, { Component } from "react";
import {
  Table,
  Button,
  DatePicker,
  Input,
  Tag,
  Badge,
  Modal,
  Tabs,
  Card,
  Tooltip,
  notification,
  Spin,
  Radio,
  Popover,
} from "antd";
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import {
  getDailyCardAuditAPI,
  recomputeDailyAuditAPI,
  updateAnomalyStatusAPI,
  getItemizedSoldCardsAPI,
} from "../../Graphs/Report/dailyCardAuditAPI";
import { formatePrice } from "../../utils/general";
import "./DailyCardAuditReport.scss";

const { TabPane } = Tabs;
const { TextArea } = Input;

class DailyCardAuditReport extends Component {
  constructor(props) {
    super(props);
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    this.state = {
      selectedDate: yesterday,
      isLoading: false,
      isRecomputing: false,
      activeTab: "cards",
      snapshot: null,
      breakdownByCard: [],
      breakdownBySource: {},
      breakdownByBatch: [],
      anomalies: [],
      // Filters
      searchTerm: "",
      batchSearchTerm: "",
      anomalyStatusFilter: "all",
      // Resolution Modal
      isModalOpen: false,
      activeAnomaly: null,
      modalStatus: "investigated",
      modalNotes: "",
      isSavingAnomaly: false,
      // Sold Cards Drilldown Modal
      isCardsModalOpen: false,
      cardsModalLoading: false,
      selectedCardRecord: null,
      itemizedCards: [],
      cardsPagination: {
        current: 1,
        pageSize: 15,
        total: 0,
      },
      cardsSearchTerm: "",
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchAuditData(this.state.selectedDate);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback);
    }
  };

  fetchAuditData = async (dateStr, forceRecompute = false) => {
    try {
      this.safeSetState({ isLoading: true });
      const { authToken, history } = this.props;

      const response = await getDailyCardAuditAPI(authToken, {
        date: dateStr,
        forceRecompute,
      });

      const payload =
        response && response.data && response.data.data
          ? response.data.data
          : response && response.data
          ? response.data
          : {};

      if (
        response &&
        (response.status === 200 || response.status === "200") &&
        (payload.snapshot || payload.breakdowns)
      ) {
        const { snapshot, breakdowns, anomalies } = payload;
        this.safeSetState({
          snapshot: snapshot || null,
          breakdownByCard: breakdowns?.by_card || [],
          breakdownBySource: breakdowns?.by_source || {},
          breakdownByBatch: breakdowns?.by_batch || [],
          anomalies: anomalies || [],
        });
      } else if (response && response.status === 401) {
        history.push(`${process.env.PUBLIC_URL}/login`);
      } else {
        notification.error({
          message: "Audit Report Error",
          description: response?.message || "Failed to load audit data.",
        });
      }
    } catch (error) {
      console.error("fetchAuditData error:", error);
      notification.error({
        message: "Audit Report Error",
        description: error.message || "Failed to connect to audit service.",
      });
    } finally {
      this.safeSetState({ isLoading: false, isRecomputing: false });
    }
  };

  handleRecompute = async () => {
    try {
      this.safeSetState({ isRecomputing: true });
      const { authToken, history } = this.props;
      const { selectedDate } = this.state;

      const response = await recomputeDailyAuditAPI(authToken, {
        date: selectedDate,
      });

      const payload =
        response && response.data && response.data.data
          ? response.data.data
          : response && response.data
          ? response.data
          : {};

      if (
        response &&
        (response.status === 200 || response.status === "200") &&
        (payload.snapshot || payload.breakdowns)
      ) {
        const { snapshot, breakdowns, anomalies } = payload;
        this.safeSetState({
          snapshot: snapshot || null,
          breakdownByCard: breakdowns?.by_card || [],
          breakdownBySource: breakdowns?.by_source || {},
          breakdownByBatch: breakdowns?.by_batch || [],
          anomalies: anomalies || [],
        });
        notification.success({
          message: "Audit Recalculated",
          description: `Successfully recalculated data for ${selectedDate}.`,
        });
      } else if (response && response.status === 401) {
        history.push(`${process.env.PUBLIC_URL}/login`);
      } else {
        notification.error({
          message: "Recalculate Failed",
          description: response?.message || "Could not recompute audit.",
        });
      }
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.message || "Something went wrong during recalculation.",
      });
    } finally {
      this.safeSetState({ isRecomputing: false });
    }
  };

  handleDateChange = (date, dateString) => {
    if (!dateString) return;
    this.setState({ selectedDate: dateString }, () => {
      this.fetchAuditData(dateString);
    });
  };

  handleQuickDate = (daysAgo) => {
    const targetDate = moment().subtract(daysAgo, "days").format("YYYY-MM-DD");
    this.setState({ selectedDate: targetDate }, () => {
      this.fetchAuditData(targetDate);
    });
  };

  openResolutionModal = (anomaly) => {
    this.setState({
      isModalOpen: true,
      activeAnomaly: anomaly,
      modalStatus: anomaly.status === "unresolved" ? "investigated" : anomaly.status,
      modalNotes: anomaly.resolution_notes || "",
    });
  };

  closeResolutionModal = () => {
    this.setState({
      isModalOpen: false,
      activeAnomaly: null,
      modalNotes: "",
      isSavingAnomaly: false,
    });
  };

  handleSaveResolution = async () => {
    const { activeAnomaly, modalStatus, modalNotes } = this.state;
    const { authToken } = this.props;
    if (!activeAnomaly) return;

    try {
      this.safeSetState({ isSavingAnomaly: true });
      const response = await updateAnomalyStatusAPI(authToken, activeAnomaly.id, {
        status: modalStatus,
        resolution_notes: modalNotes,
      });

      if (response && (response.status === 200 || response.status === "200")) {
        notification.success({
          message: "Anomaly Updated",
          description: `Status changed to ${modalStatus}.`,
        });
        this.closeResolutionModal();
        this.fetchAuditData(this.state.selectedDate);
      } else {
        notification.error({
          message: "Update Failed",
          description: response?.message || "Could not update anomaly.",
        });
      }
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.message || "Failed to update anomaly status.",
      });
    } finally {
      this.safeSetState({ isSavingAnomaly: false });
    }
  };

  openCardsModal = (record) => {
    this.setState(
      {
        isCardsModalOpen: true,
        selectedCardRecord: record,
        cardsSearchTerm: "",
        cardsPagination: { current: 1, pageSize: 15, total: 0 },
        itemizedCards: [],
      },
      () => {
        this.fetchItemizedCards(record, 1, 15, "");
      }
    );
  };

  closeCardsModal = () => {
    this.setState({
      isCardsModalOpen: false,
      selectedCardRecord: null,
      itemizedCards: [],
      cardsSearchTerm: "",
    });
  };

  fetchItemizedCards = async (record, page = 1, pageSize = 15, search = "") => {
    const { authToken } = this.props;
    const { selectedDate } = this.state;
    const rec = record || this.state.selectedCardRecord;
    if (!rec) return;

    try {
      this.safeSetState({ cardsModalLoading: true });

      let batchParam = rec.batch_number;
      if (
        !batchParam ||
        String(batchParam).includes("Batches") ||
        batchParam === "multiple" ||
        batchParam === "all"
      ) {
        batchParam = "all";
      }

      let sourceParam = rec.source;
      if (!sourceParam || sourceParam === "multiple" || sourceParam === "all") {
        sourceParam = "all";
      }

      const payload = {
        date: selectedDate,
        card_name: rec.card_name,
        batch_number: batchParam,
        source: sourceParam,
        page,
        limit: pageSize,
        search,
      };

      const response = await getItemizedSoldCardsAPI(authToken, payload);
      const resData =
        response && response.data && response.data.data
          ? response.data.data
          : response && response.data
          ? response.data
          : {};

      if (response && (response.status === 200 || response.status === "200")) {
        const items = resData.items || [];
        const total = resData.total || 0;
        this.safeSetState({
          itemizedCards: items,
          cardsPagination: {
            current: page,
            pageSize,
            total,
          },
        });
      } else {
        notification.error({
          message: "Error Fetching Cards",
          description: (response && response.message) || "Failed to load sold cards.",
        });
      }
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.message || "Failed to load sold cards.",
      });
    } finally {
      this.safeSetState({ cardsModalLoading: false });
    }
  };

  handleCardsTableChange = (pagination) => {
    const { selectedCardRecord, cardsSearchTerm } = this.state;
    this.fetchItemizedCards(
      selectedCardRecord,
      pagination.current,
      pagination.pageSize,
      cardsSearchTerm
    );
  };

  handleCardsSearch = (val) => {
    const { selectedCardRecord, cardsPagination } = this.state;
    this.setState({ cardsSearchTerm: val }, () => {
      this.fetchItemizedCards(selectedCardRecord, 1, cardsPagination.pageSize, val);
    });
  };

  exportToCSV = () => {
    const { breakdownByCard, selectedDate } = this.state;
    if (!breakdownByCard.length) {
      return notification.warning({
        message: "Export Warning",
        description: "No card breakdown data available to export.",
      });
    }

    const headers = ["Card Name", "Quantity Sold", "Gross Sales", "Cost Basis", "Net Profit", "Margin %"];
    const rows = breakdownByCard.map((c) => [
      `"${String(c.card_name || "").replace(/"/g, '""')}"`,
      c.qty || c.card_count || 0,
      Number(c.gross_sales || 0).toFixed(2),
      Number(c.cost || 0).toFixed(2),
      Number(c.profit || 0).toFixed(2),
      `${Number(c.margin_pct || 0).toFixed(2)}%`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `daily_card_sales_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  exportBatchToCSV = () => {
    const { breakdownByBatch, selectedDate } = this.state;
    if (!breakdownByBatch.length) {
      return notification.warning({
        message: "Export Warning",
        description: "No batch breakdown data available to export.",
      });
    }

    const headers = [
      "Batch Number",
      "Card Name",
      "Source",
      "Cards Sold",
      "Gross Sales",
      "Cost Basis",
      "Net Profit",
      "Margin %",
    ];
    const rows = breakdownByBatch.map((b) => [
      `"${String(b.batch_number || "").replace(/"/g, '""')}"`,
      `"${String(b.card_name || "").replace(/"/g, '""')}"`,
      `"${String(b.source || "").replace(/"/g, '""')}"`,
      b.card_count || b.qty || 0,
      Number(b.gross_sales || 0).toFixed(2),
      Number(b.cost || 0).toFixed(2),
      Number(b.profit || 0).toFixed(2),
      `${Number(b.margin_pct || 0).toFixed(2)}%`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `daily_batch_sales_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Progressive Disclosure Popovers ────────────────────────────────────────
  renderChannelSplitPopover(metric = "qty") {
    const { breakdownBySource, snapshot } = this.state;
    const channels = [
      { key: "admin", label: "Admin Warehouse", tagColor: "blue" },
      { key: "sub_admin", label: "Sub-Admin Stock", tagColor: "purple" },
      { key: "company", label: "Company Direct", tagColor: "orange" },
      { key: "mint_api", label: "Mint API (Digital)", tagColor: "cyan" },
      { key: "like_api", label: "Like Card API", tagColor: "geekblue" },
      { key: "khales_api", label: "Khales API", tagColor: "magenta" },
      { key: "other", label: "Other Channels", tagColor: "default" },
    ];

    const totalSold = snapshot ? snapshot.total_cards_sold : 0;

    return (
      <div style={{ minWidth: 290, maxWidth: 380, padding: 4 }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #E2E8F0", color: "#64748B", textAlign: "left" }}>
              <th style={{ padding: "4px 6px" }}>Channel</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Cards</th>
              {metric !== "qty" && (
                <th style={{ padding: "4px 6px", textAlign: "right" }}>
                  {metric === "sales" ? "Revenue" : metric === "cost" ? "Cost" : "Profit"}
                </th>
              )}
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((ch) => {
              const data =
                breakdownBySource && breakdownBySource[ch.key]
                  ? breakdownBySource[ch.key]
                  : { qty: 0, gross_sales: 0, cost: 0, profit: 0 };
              if (data.qty === 0 && data.gross_sales === 0) return null;
              const sharePct = totalSold > 0 ? ((data.qty / totalSold) * 100).toFixed(1) : "0.0";
              return (
                <tr key={ch.key} style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "6px 6px" }}>
                    <Tag color={ch.tagColor} style={{ marginRight: 0, fontSize: 11 }}>
                      {ch.label}
                    </Tag>
                  </td>
                  <td style={{ padding: "6px 6px", textAlign: "right", fontWeight: 600 }}>
                    {data.qty.toLocaleString()}
                  </td>
                  {metric === "sales" && (
                    <td style={{ padding: "6px 6px", textAlign: "right", fontWeight: 600, color: "#0F172A" }}>
                      {formatePrice(data.gross_sales)}
                    </td>
                  )}
                  {metric === "cost" && (
                    <td style={{ padding: "6px 6px", textAlign: "right", fontWeight: 600, color: "#64748B" }}>
                      {formatePrice(data.cost)}
                    </td>
                  )}
                  {metric === "profit" && (
                    <td
                      style={{
                        padding: "6px 6px",
                        textAlign: "right",
                        fontWeight: 600,
                        color: data.profit >= 0 ? "#16A34A" : "#DC2626",
                      }}
                    >
                      {data.profit >= 0 ? `+${formatePrice(data.profit)}` : formatePrice(data.profit)}
                    </td>
                  )}
                  <td style={{ padding: "6px 6px", textAlign: "right", color: "#64748B" }}>
                    {sharePct}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderCardDetailsPopover(record) {
    const sourceBreakdown = record.source_breakdown || {};
    const batchBreakdown = record.batch_breakdown || [];
    const sources = [
      { key: "admin", label: "Admin Warehouse", tagColor: "blue" },
      { key: "sub_admin", label: "Sub-Admin", tagColor: "purple" },
      { key: "company", label: "Company", tagColor: "orange" },
      { key: "mint_api", label: "Mint API", tagColor: "cyan" },
      { key: "like_api", label: "Like API", tagColor: "geekblue" },
      { key: "khales_api", label: "Khales API", tagColor: "magenta" },
      { key: "other", label: "Other", tagColor: "default" },
    ];

    return (
      <div style={{ minWidth: 290, maxWidth: 360, padding: 4 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", marginBottom: 6 }}>
            Inventory Sources
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {sources.map((s) => {
              const count = sourceBreakdown[s.key] || 0;
              if (count === 0) return null;
              return (
                <Tag key={s.key} color={s.tagColor} style={{ fontSize: 11, padding: "2px 8px" }}>
                  {s.label}: <strong>{count.toLocaleString()}</strong>
                </Tag>
              );
            })}
          </div>
        </div>

        {batchBreakdown.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", marginBottom: 6 }}>
              Batches Dispatched ({batchBreakdown.length})
            </div>
            <div style={{ maxHeight: 150, overflowY: "auto" }}>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #E2E8F0", color: "#64748B" }}>
                    <th style={{ textAlign: "left", padding: "3px 4px" }}>Batch</th>
                    <th style={{ textAlign: "right", padding: "3px 4px" }}>Cards</th>
                    <th style={{ textAlign: "right", padding: "3px 4px" }}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {batchBreakdown.map((b, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "4px 4px", fontFamily: "monospace", color: "#2563EB", fontWeight: 600 }}>
                        {b.batch_number}
                      </td>
                      <td style={{ padding: "4px 4px", textAlign: "right", fontWeight: 600 }}>
                        {b.count.toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: "4px 4px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: b.profit >= 0 ? "#16A34A" : "#DC2626",
                        }}
                      >
                        {formatePrice(b.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── KPI Banner ────────────────────────────────────────────────────────────
  renderKPIBanner() {
    const { snapshot, anomalies } = this.state;
    const totalSold = snapshot ? snapshot.total_cards_sold : 0;
    const grossSales = snapshot ? snapshot.total_gross_sales : 0;
    const totalCost = snapshot ? snapshot.total_cost : 0;
    const netProfit = snapshot ? snapshot.net_profit : 0;
    const marginPct = snapshot ? snapshot.profit_margin_pct : 0;
    const unresolvedCount = anomalies.filter((a) => a.status === "unresolved").length;
    const isHealthy = unresolvedCount === 0;

    return (
      <div>
        {/* Health ribbon */}
        <div className={`daily-audit-health-ribbon ${isHealthy ? "ribbon-healthy" : "ribbon-flagged"}`}>
          <div className="ribbon-left">
            <span className={`ribbon-icon ${isHealthy ? "icon-healthy" : "icon-flagged"}`}>
              {isHealthy ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
            </span>
            <div>
              <div className={`ribbon-text-primary ${isHealthy ? "text-healthy" : "text-flagged"}`}>
                {isHealthy
                  ? "ALL SYSTEMS AUDITED & HEALTHY"
                  : `ATTENTION: ${unresolvedCount} UNRESOLVED MARGIN ANOMALIES DETECTED`}
              </div>
              <div className={`ribbon-text-secondary ${isHealthy ? "text-healthy" : "text-flagged"}`}>
                {isHealthy
                  ? "Zero negative or zero-margin transactions detected for this period."
                  : "Some transactions were sold at or below cost price. Please inspect the Margin Exceptions queue."}
              </div>
            </div>
          </div>
          <div>
            <Tag color={snapshot && snapshot.computed_by === "admin_recompute" ? "blue" : "purple"}>
              {snapshot && snapshot.computed_by === "admin_recompute" ? "Manual Recompute" : "Nightly Cron"}
            </Tag>
          </div>
        </div>

        {/* KPI bar with (i) progressive disclosure popups */}
        <div className="daily-audit-kpi-grid">
          <div className="daily-audit-kpi-bar">
            {/* 1. Cards Sold */}
            <div className="daily-audit-kpi-item">
              <div className="daily-audit-kpi-label-row">
                <span className="daily-audit-kpi-label">Cards Sold</span>
                <Popover
                  content={this.renderChannelSplitPopover("qty")}
                  title={<span style={{ fontWeight: 600 }}>Cards Sold by Channel</span>}
                  trigger="click"
                  placement="bottom"
                >
                  <Tooltip title="Click to view channel breakdown">
                    <Button
                      type="text"
                      size="small"
                      className="daily-audit-hint-btn"
                      icon={<InfoCircleOutlined />}
                      aria-label="View cards breakdown"
                    />
                  </Tooltip>
                </Popover>
              </div>
              <span className="daily-audit-kpi-value">{totalSold.toLocaleString()}</span>
              <span className="daily-audit-kpi-meta">Total vouchers dispatched</span>
            </div>

            <div className="daily-audit-kpi-divider" />

            {/* 2. Gross Revenue */}
            <div className="daily-audit-kpi-item">
              <div className="daily-audit-kpi-label-row">
                <span className="daily-audit-kpi-label">Gross Revenue</span>
                <Popover
                  content={this.renderChannelSplitPopover("sales")}
                  title={<span style={{ fontWeight: 600 }}>Revenue by Channel</span>}
                  trigger="click"
                  placement="bottom"
                >
                  <Tooltip title="Click to view revenue breakdown">
                    <Button
                      type="text"
                      size="small"
                      className="daily-audit-hint-btn"
                      icon={<InfoCircleOutlined />}
                      aria-label="View revenue breakdown"
                    />
                  </Tooltip>
                </Popover>
              </div>
              <span className="daily-audit-kpi-value">LYD {formatePrice(grossSales)}</span>
              <span className="daily-audit-kpi-meta">Total billed to retail users</span>
            </div>

            <div className="daily-audit-kpi-divider" />

            {/* 3. Cost of Goods */}
            <div className="daily-audit-kpi-item">
              <div className="daily-audit-kpi-label-row">
                <span className="daily-audit-kpi-label">Cost of Goods (COGS)</span>
                <Popover
                  content={this.renderChannelSplitPopover("cost")}
                  title={<span style={{ fontWeight: 600 }}>Cost Basis by Channel</span>}
                  trigger="click"
                  placement="bottom"
                >
                  <Tooltip title="Click to view cost breakdown">
                    <Button
                      type="text"
                      size="small"
                      className="daily-audit-hint-btn"
                      icon={<InfoCircleOutlined />}
                      aria-label="View cost breakdown"
                    />
                  </Tooltip>
                </Popover>
              </div>
              <span className="daily-audit-kpi-value">LYD {formatePrice(totalCost)}</span>
              <span className="daily-audit-kpi-meta">Exact supplier ledger cost</span>
            </div>

            <div className="daily-audit-kpi-divider" />

            {/* 4. Net Admin Profit */}
            <div className="daily-audit-kpi-item">
              <div className="daily-audit-kpi-label-row">
                <span className="daily-audit-kpi-label">Net Admin Profit</span>
                <Popover
                  content={this.renderChannelSplitPopover("profit")}
                  title={<span style={{ fontWeight: 600 }}>Net Profit by Channel</span>}
                  trigger="click"
                  placement="bottom"
                >
                  <Tooltip title="Click to view profit breakdown">
                    <Button
                      type="text"
                      size="small"
                      className="daily-audit-hint-btn"
                      icon={<InfoCircleOutlined />}
                      aria-label="View profit breakdown"
                    />
                  </Tooltip>
                </Popover>
              </div>
              <span className={`daily-audit-kpi-value ${netProfit >= 0 ? "value-positive" : "value-negative"}`}>
                LYD {formatePrice(netProfit)}
              </span>
              <span className="daily-audit-kpi-meta">
                Margin:{" "}
                <strong className={netProfit >= 0 ? "daily-audit-profit-positive" : "daily-audit-profit-negative"}>
                  {Number(marginPct || 0).toFixed(2)}%
                </strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Card-Wise Sales Tab ───────────────────────────────────────────────────
  renderCardSalesTab() {
    const { breakdownByCard, searchTerm } = this.state;

    const filteredData = breakdownByCard.filter((c) =>
      c.card_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
      {
        title: "Card Product",
        dataIndex: "card_name",
        key: "card_name",
        render: (name) => <span style={{ fontWeight: 500, color: "#0F172A" }}>{name}</span>,
      },
      {
        title: "Cards Sold",
        dataIndex: "card_count",
        key: "card_count",
        align: "right",
        sorter: (a, b) => (a.card_count || a.qty) - (b.card_count || b.qty),
        render: (cnt, item) => {
          const count = cnt !== undefined ? cnt : item.qty;
          const hasDetails =
            (item.batch_breakdown && item.batch_breakdown.length > 0) ||
            (item.source_breakdown && Object.values(item.source_breakdown).some((v) => v > 0));
          return (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 600 }}>{count.toLocaleString()}</span>
              {hasDetails && (
                <Popover
                  content={this.renderCardDetailsPopover(item)}
                  title={<span style={{ fontWeight: 600 }}>{item.card_name} — Sales Sources &amp; Batches</span>}
                  trigger="click"
                  placement="right"
                >
                  <Tooltip title="Click to view sources & batches for this card">
                    <Button
                      type="text"
                      size="small"
                      className="daily-audit-hint-btn"
                      icon={<InfoCircleOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Popover>
              )}
            </div>
          );
        },
      },
      {
        title: "Gross Sales (LYD)",
        dataIndex: "gross_sales",
        key: "gross_sales",
        align: "right",
        sorter: (a, b) => a.gross_sales - b.gross_sales,
        render: (val) => formatePrice(val),
      },
      {
        title: "Cost Basis (LYD)",
        dataIndex: "cost",
        key: "cost",
        align: "right",
        sorter: (a, b) => a.cost - b.cost,
        render: (val) => formatePrice(val),
      },
      {
        title: "Net Profit (LYD)",
        dataIndex: "profit",
        key: "profit",
        align: "right",
        sorter: (a, b) => a.profit - b.profit,
        render: (val) => (
          <span className={val >= 0 ? "daily-audit-profit-positive" : "daily-audit-profit-negative"}>
            {val >= 0 ? `+${formatePrice(val)}` : formatePrice(val)}
          </span>
        ),
      },
      {
        title: "Margin %",
        dataIndex: "margin_pct",
        key: "margin_pct",
        align: "right",
        sorter: (a, b) => parseFloat(a.margin_pct) - parseFloat(b.margin_pct),
        render: (pct) => {
          const val = parseFloat(pct);
          let color = "green";
          if (val < 0) color = "red";
          else if (val < 3) color = "orange";
          return <Tag color={color}>{val.toFixed(2)}%</Tag>;
        },
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              this.openCardsModal(record);
            }}
          >
            View Sold Cards
          </Button>
        ),
      },
    ];

    return (
      <div>
        <div className="daily-audit-tab-toolbar">
          <div className="toolbar-left">
            <Input
              prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
              placeholder="Search card product..."
              style={{ width: 300 }}
              value={searchTerm}
              onChange={(e) => this.setState({ searchTerm: e.target.value })}
              allowClear
            />
          </div>
          <div className="toolbar-right">
            <span className="toolbar-hint">💡 Click any row to view individual sold cards &amp; serials</span>
            <Button icon={<DownloadOutlined />} onClick={this.exportToCSV}>
              Export CSV
            </Button>
          </div>
        </div>
        <Table
          className="daily-audit-table"
          columns={columns}
          dataSource={filteredData}
          rowKey={(r) => r.card_id || r.card_name}
          pagination={{ pageSize: 10 }}
          bordered={false}
          size="middle"
          onRow={(record) => ({
            onClick: () => this.openCardsModal(record),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <div className="daily-audit-empty-state">
                <strong>No card sales data available</strong>
                <span>Select a different date to view card sales data.</span>
              </div>
            ),
          }}
        />
      </div>
    );
  }

  // ─── Batch-Wise Sales Tab ──────────────────────────────────────────────────
  renderBatchSalesTab() {
    const { breakdownByBatch, batchSearchTerm } = this.state;

    const filteredBatches = breakdownByBatch.filter((b) => {
      const q = (batchSearchTerm || "").toLowerCase();
      const matchBatch = b.batch_number && b.batch_number.toLowerCase().includes(q);
      const matchCard = b.card_name && b.card_name.toLowerCase().includes(q);
      const matchSource = b.source && b.source.toLowerCase().includes(q);
      return !q || matchBatch || matchCard || matchSource;
    });

    const columns = [
      {
        title: "Batch Number",
        dataIndex: "batch_number",
        key: "batch_number",
        render: (batch) => {
          if (!batch || batch === "API/Digital" || batch === "Digital / API") {
            return <Tag color="default">Digital / API</Tag>;
          }
          return (
            <Tag color="blue" style={{ fontFamily: "monospace", fontWeight: 600 }}>
              {batch}
            </Tag>
          );
        },
      },
      {
        title: "Card Product",
        dataIndex: "card_name",
        key: "card_name",
        render: (name) => <span style={{ fontWeight: 500, color: "#0F172A" }}>{name}</span>,
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        render: (s) => {
          const val = (s || "admin").toLowerCase();
          let tagColor = "blue";
          if (val === "sub_admin") tagColor = "purple";
          if (val === "company") tagColor = "orange";
          if (val === "mint_api") tagColor = "cyan";
          if (val === "like_api") tagColor = "geekblue";
          if (val === "khales_api") tagColor = "magenta";
          return <Tag color={tagColor}>{val.toUpperCase()}</Tag>;
        },
      },
      {
        title: "Cards Sold",
        dataIndex: "card_count",
        key: "card_count",
        align: "right",
        sorter: (a, b) => (a.card_count || a.qty) - (b.card_count || b.qty),
        render: (cnt, item) => {
          const count = cnt !== undefined ? cnt : item.qty;
          return <span style={{ fontWeight: 600 }}>{count.toLocaleString()}</span>;
        },
      },
      {
        title: "Gross Sales (LYD)",
        dataIndex: "gross_sales",
        key: "gross_sales",
        align: "right",
        sorter: (a, b) => a.gross_sales - b.gross_sales,
        render: (val) => formatePrice(val),
      },
      {
        title: "Cost Basis (LYD)",
        dataIndex: "cost",
        key: "cost",
        align: "right",
        sorter: (a, b) => a.cost - b.cost,
        render: (val) => formatePrice(val),
      },
      {
        title: "Net Profit (LYD)",
        dataIndex: "profit",
        key: "profit",
        align: "right",
        sorter: (a, b) => a.profit - b.profit,
        render: (val) => (
          <span className={val >= 0 ? "daily-audit-profit-positive" : "daily-audit-profit-negative"}>
            {val >= 0 ? `+${formatePrice(val)}` : formatePrice(val)}
          </span>
        ),
      },
      {
        title: "Margin %",
        dataIndex: "margin_pct",
        key: "margin_pct",
        align: "right",
        sorter: (a, b) => parseFloat(a.margin_pct) - parseFloat(b.margin_pct),
        render: (pct) => {
          const val = parseFloat(pct);
          let color = "green";
          if (val < 0) color = "red";
          else if (val < 3) color = "orange";
          return <Tag color={color}>{val.toFixed(2)}%</Tag>;
        },
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              this.openCardsModal(record);
            }}
          >
            View Sold Cards
          </Button>
        ),
      },
    ];

    return (
      <div>
        <div className="daily-audit-tab-toolbar">
          <div className="toolbar-left">
            <Input
              prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
              placeholder="Search by batch number, card name, or source..."
              style={{ width: 340 }}
              value={batchSearchTerm}
              onChange={(e) => this.setState({ batchSearchTerm: e.target.value })}
              allowClear
            />
          </div>
          <div className="toolbar-right">
            <span className="toolbar-hint">💡 Click any batch row to view individual sold cards</span>
            <Button icon={<DownloadOutlined />} onClick={this.exportBatchToCSV}>
              Export Batches CSV
            </Button>
          </div>
        </div>
        <Table
          className="daily-audit-table"
          columns={columns}
          dataSource={filteredBatches}
          rowKey={(r) => `${r.batch_number}_${r.card_name}_${r.source}`}
          pagination={{ pageSize: 10 }}
          bordered={false}
          size="middle"
          onRow={(record) => ({
            onClick: () => this.openCardsModal(record),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <div className="daily-audit-empty-state">
                <strong>No batch data available</strong>
                <span>Select a different date or clear filters to view batch sales.</span>
              </div>
            ),
          }}
        />
      </div>
    );
  }

  // ─── Anomalies Tab ──────────────────────────────────────────────────────────
  renderAnomaliesTab() {
    const { anomalies, anomalyStatusFilter } = this.state;

    const filteredAnomalies = anomalies.filter((a) => {
      if (anomalyStatusFilter === "all") return true;
      return a.status === anomalyStatusFilter;
    });

    const columns = [
      {
        title: "Txn ID",
        dataIndex: "transaction_id",
        key: "transaction_id",
        render: (id) => <code style={{ fontSize: 11 }}>{id}</code>,
      },
      {
        title: "Card Name",
        dataIndex: "card_name",
        key: "card_name",
        render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>,
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        render: (s) => {
          let tagColor = "blue";
          if (s === "sub_admin") tagColor = "purple";
          if (s === "company") tagColor = "orange";
          if (s === "mint_api") tagColor = "cyan";
          if (s === "like_api") tagColor = "geekblue";
          if (s === "khales_api") tagColor = "magenta";
          return <Tag color={tagColor}>{s.toUpperCase()}</Tag>;
        },
      },
      {
        title: "Buyer & City",
        key: "buyer",
        render: (_, item) => (
          <div>
            <div style={{ fontWeight: 500 }}>{item.buyer_name || "Unknown"}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>{item.city || "N/A"}</div>
          </div>
        ),
      },
      {
        title: "Sell Price",
        dataIndex: "sell_price",
        key: "sell_price",
        align: "right",
        render: (v) => `LYD ${formatePrice(v)}`,
      },
      {
        title: "Cost Price",
        dataIndex: "cost_price",
        key: "cost_price",
        align: "right",
        render: (v) => `LYD ${formatePrice(v)}`,
      },
      {
        title: "Margin / Deficit",
        dataIndex: "margin_amount",
        key: "margin_amount",
        align: "right",
        render: (m) => (
          <span className={m < 0 ? "daily-audit-profit-negative" : ""} style={{ fontWeight: 700, color: m < 0 ? undefined : "#D97706" }}>
            {m < 0 ? formatePrice(m) : "0.00 (Zero Margin)"}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        render: (status) => {
          if (status === "resolved") return <Tag color="green">RESOLVED</Tag>;
          if (status === "investigated") return <Tag color="blue">INVESTIGATED</Tag>;
          return <Tag color="red">UNRESOLVED</Tag>;
        },
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={() => this.openResolutionModal(record)}
          >
            Review & Resolve
          </Button>
        ),
      },
    ];

    return (
      <div>
        <div className="daily-audit-anomaly-filter">
          <span className="filter-label">Filter by Status:</span>
          <Radio.Group
            value={anomalyStatusFilter}
            onChange={(e) => this.setState({ anomalyStatusFilter: e.target.value })}
            buttonStyle="solid"
          >
            <Radio.Button value="all">All ({anomalies.length})</Radio.Button>
            <Radio.Button value="unresolved">
              Unresolved ({anomalies.filter((a) => a.status === "unresolved").length})
            </Radio.Button>
            <Radio.Button value="investigated">
              Investigated ({anomalies.filter((a) => a.status === "investigated").length})
            </Radio.Button>
            <Radio.Button value="resolved">
              Resolved ({anomalies.filter((a) => a.status === "resolved").length})
            </Radio.Button>
          </Radio.Group>
        </div>
        <Table
          className="daily-audit-table"
          columns={columns}
          dataSource={filteredAnomalies}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          bordered={false}
          size="middle"
          locale={{ emptyText: "No margin anomalies detected for this date! 🎉" }}
        />
      </div>
    );
  }

  // ─── Channel Split Tab ──────────────────────────────────────────────────────
  renderChannelSplitTab() {
    const { breakdownBySource } = this.state;
    const channels = [
      { key: "admin", label: "Admin Central", icon: "🏛️" },
      { key: "sub_admin", label: "Sub-Admin", icon: "🏢" },
      { key: "company", label: "Company Direct", icon: "🤝" },
      { key: "mint_api", label: "Mint API", icon: "⚡" },
      { key: "like_api", label: "Like Card API", icon: "🌐" },
      { key: "khales_api", label: "Khales API", icon: "💳" },
    ];

    return (
      <div className="daily-audit-channel-grid">
        {channels.map((ch) => {
          const data = breakdownBySource[ch.key] || { qty: 0, gross_sales: 0, cost: 0, profit: 0 };
          const margin = data.gross_sales > 0 ? ((data.profit / data.gross_sales) * 100).toFixed(2) : 0;
          return (
            <Card
              key={ch.key}
              className="daily-audit-channel-card"
              title={
                <span>
                  <span style={{ marginRight: 8 }}>{ch.icon}</span>
                  {ch.label}
                </span>
              }
              bordered
            >
              <div className="channel-row">
                <span className="channel-label">Cards Sold:</span>
                <strong className="channel-value">{data.qty.toLocaleString()}</strong>
              </div>
              <div className="channel-row">
                <span className="channel-label">Gross Sales:</span>
                <strong className="channel-value">LYD {formatePrice(data.gross_sales)}</strong>
              </div>
              <div className="channel-row">
                <span className="channel-label">Total Cost:</span>
                <strong className="channel-value">LYD {formatePrice(data.cost)}</strong>
              </div>
              <div className="channel-row">
                <span className="channel-label" style={{ fontWeight: 500 }}>Net Profit:</span>
                <strong className={data.profit >= 0 ? "channel-profit-positive" : "channel-profit-negative"}>
                  LYD {formatePrice(data.profit)} ({margin}%)
                </strong>
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  // ─── Resolution Modal ───────────────────────────────────────────────────────
  renderResolutionModal() {
    const { isModalOpen, activeAnomaly, modalStatus, modalNotes, isSavingAnomaly } = this.state;
    if (!activeAnomaly) return null;

    return (
      <Modal
        wrapClassName="daily-audit-modal"
        title={`Audit Resolution: ${activeAnomaly.transaction_id}`}
        visible={isModalOpen}
        onCancel={this.closeResolutionModal}
        footer={[
          <Button key="cancel" onClick={this.closeResolutionModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSavingAnomaly}
            onClick={this.handleSaveResolution}
          >
            Save Resolution
          </Button>,
        ]}
        destroyOnClose
        width={520}
      >
        <div>
          <div className="daily-audit-resolution-details">
            <div className="detail-item">
              <span className="detail-label">Card Product:</span>
              <span className="detail-value">{activeAnomaly.card_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Source:</span>
              <span className="detail-value">{activeAnomaly.source.toUpperCase()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Buyer:</span>
              <span className="detail-value">{activeAnomaly.buyer_name || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Batch:</span>
              <span className="detail-value">{activeAnomaly.batch_number || "API"}</span>
            </div>
          </div>

          <div className="daily-audit-margin-summary">
            <div className="summary-row">
              <span>Recorded Cost:</span>
              <strong>LYD {formatePrice(activeAnomaly.cost_price)}</strong>
            </div>
            <div className="summary-row">
              <span>Selling Price:</span>
              <strong>LYD {formatePrice(activeAnomaly.sell_price)}</strong>
            </div>
            <div className="summary-row">
              <span>Net Loss / Margin:</span>
              <span>LYD {formatePrice(activeAnomaly.margin_amount)}</span>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>Resolution Status:</label>
            <Radio.Group
              value={modalStatus}
              onChange={(e) => this.setState({ modalStatus: e.target.value })}
            >
              <Radio value="investigated">Investigated (Root cause confirmed)</Radio>
              <Radio value="resolved">Resolved (Issue closed / adjusted)</Radio>
            </Radio.Group>
          </div>

          <div>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>Audit Notes:</label>
            <TextArea
              rows={3}
              placeholder="e.g. Promotional discount authorized by admin; supplier price updated."
              value={modalNotes}
              onChange={(e) => this.setState({ modalNotes: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    );
  }

  // ─── Sold Cards Drilldown Modal ─────────────────────────────────────────────
  renderSoldCardsModal() {
    const {
      isCardsModalOpen,
      cardsModalLoading,
      selectedCardRecord,
      itemizedCards,
      cardsPagination,
      cardsSearchTerm,
      selectedDate,
    } = this.state;

    const columns = [
      {
        title: "#",
        key: "index",
        width: 55,
        align: "center",
        render: (text, record, index) =>
          (cardsPagination.current - 1) * cardsPagination.pageSize + index + 1,
      },
      {
        title: "Serial Number",
        dataIndex: "serial_number",
        key: "serial_number",
        render: (serial) => (
          <Tag color="geekblue" style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 500 }}>
            {serial || "N/A"}
          </Tag>
        ),
      },
      {
        title: "Transaction ID",
        dataIndex: "transaction_id",
        key: "transaction_id",
        render: (txn) => (
          <code style={{ fontSize: 12, backgroundColor: "#F1F5F9", padding: "2px 6px", borderRadius: 4 }}>
            {txn}
          </code>
        ),
      },
      {
        title: "Batch #",
        dataIndex: "batch_number",
        key: "batch_number",
        render: (batch) => {
          if (!batch || batch === "API/Digital" || batch === "Digital / API") {
            return <Tag color="default">Digital / API</Tag>;
          }
          return (
            <Tag color="blue" style={{ fontFamily: "monospace" }}>
              {batch}
            </Tag>
          );
        },
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        render: (s) => {
          const val = (s || "admin").toLowerCase();
          let tagColor = "blue";
          if (val === "sub_admin") tagColor = "purple";
          if (val === "mint_api") tagColor = "cyan";
          if (val === "like_api") tagColor = "geekblue";
          if (val === "khales_api") tagColor = "magenta";
          return <Tag color={tagColor}>{val.toUpperCase()}</Tag>;
        },
      },
      {
        title: "Buyer & Location",
        key: "buyer",
        render: (_, r) => (
          <div>
            <div style={{ fontWeight: 500, color: "#0F172A" }}>
              <UserOutlined style={{ marginRight: 6, color: "#64748B" }} />
              {r.buyer_name || "Unknown Buyer"}
            </div>
            {r.city && r.city !== "N/A" && (
              <div style={{ fontSize: 12, color: "#64748B", marginLeft: 20 }}>{r.city}</div>
            )}
          </div>
        ),
      },
      {
        title: "Sell Price (LYD)",
        dataIndex: "sell_price",
        key: "sell_price",
        align: "right",
        render: (val) => formatePrice(val),
      },
      {
        title: "Cost Basis (LYD)",
        dataIndex: "cost_price",
        key: "cost_price",
        align: "right",
        render: (val) => formatePrice(val),
      },
      {
        title: "Profit (LYD)",
        dataIndex: "profit",
        key: "profit",
        align: "right",
        render: (val) => (
          <span className={val >= 0 ? "daily-audit-profit-positive" : "daily-audit-profit-negative"}>
            {val >= 0 ? `+${formatePrice(val)}` : formatePrice(val)}
          </span>
        ),
      },
      {
        title: "Sold Time",
        dataIndex: "sold_at",
        key: "sold_at",
        render: (date) => (date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "N/A"),
      },
    ];

    const cardName = selectedCardRecord ? selectedCardRecord.card_name : "Sold Cards";

    return (
      <Modal
        wrapClassName="daily-audit-modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 500, color: "#0F172A" }}>{cardName}</span>
            <Tag color="blue">Sold Cards Details</Tag>
          </div>
        }
        visible={isCardsModalOpen}
        onCancel={this.closeCardsModal}
        footer={[
          <Button key="close" type="primary" onClick={this.closeCardsModal}>
            Close
          </Button>,
        ]}
        width={1050}
        destroyOnClose
      >
        <div className="daily-audit-modal-info-bar">
          <div className="info-metrics">
            <div className="info-metric-item">
              <span className="info-metric-label">Audit Date</span>
              <span className="info-metric-value">{selectedDate}</span>
            </div>
            <div className="info-metric-item">
              <span className="info-metric-label">Total Sold Cards</span>
              <span className="info-metric-value">{cardsPagination.total.toLocaleString()} cards</span>
            </div>
            {selectedCardRecord && (
              <div className="info-metric-item">
                <span className="info-metric-label">Product Net Profit</span>
                <span
                  className={
                    selectedCardRecord.profit >= 0
                      ? "info-metric-value-positive"
                      : "info-metric-value-negative"
                  }
                >
                  {formatePrice(selectedCardRecord.profit)} LYD
                </span>
              </div>
            )}
          </div>
          <div>
            <Input.Search
              placeholder="Search serial no or txn ID..."
              allowClear
              style={{ width: 280 }}
              value={cardsSearchTerm}
              onChange={(e) => this.setState({ cardsSearchTerm: e.target.value })}
              onSearch={this.handleCardsSearch}
            />
          </div>
        </div>

        <Table
          className="daily-audit-table"
          columns={columns}
          dataSource={itemizedCards}
          rowKey={(r) => r.id || r.transaction_id || r.serial_number}
          loading={cardsModalLoading}
          pagination={{
            current: cardsPagination.current,
            pageSize: cardsPagination.pageSize,
            total: cardsPagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "15", "20", "50"],
            showTotal: (total) => `Total ${total.toLocaleString()} cards`,
          }}
          onChange={this.handleCardsTableChange}
          bordered={false}
          size="middle"
        />
      </Modal>
    );
  }

  // ─── Main Render ────────────────────────────────────────────────────────────
  render() {
    const { selectedDate, isLoading, isRecomputing, activeTab, anomalies } = this.state;
    const unresolvedCount = anomalies.filter((a) => a.status === "unresolved").length;

    const yesterdayDate = moment().subtract(1, "day").format("YYYY-MM-DD");
    const twoDaysAgoDate = moment().subtract(2, "days").format("YYYY-MM-DD");

    return (
      <div className="daily-audit-page">
        {/* Header */}
        <div className="daily-audit-page-heading">
          <div>
            <h3 className="daily-audit-title">Daily Card Sales &amp; Profit Report</h3>
          </div>
          <div className="daily-audit-heading-actions">
            <Button
              type={selectedDate === yesterdayDate ? "primary" : "default"}
              onClick={() => this.handleQuickDate(1)}
            >
              Yesterday
            </Button>
            <Button
              type={selectedDate === twoDaysAgoDate ? "primary" : "default"}
              onClick={() => this.handleQuickDate(2)}
            >
              2 Days Ago
            </Button>
            <DatePicker
              value={moment(selectedDate, "YYYY-MM-DD")}
              onChange={this.handleDateChange}
              allowClear={false}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > moment().endOf("day")}
            />
            <Button
              icon={<SyncOutlined spin={isRecomputing} />}
              loading={isRecomputing}
              onClick={this.handleRecompute}
            >
              Re-run Audit
            </Button>
          </div>
        </div>

        <Spin spinning={isLoading}>
          {/* KPI Banner */}
          {this.renderKPIBanner()}

          {/* Main Tabs Card */}
          <Card bordered={false} className="daily-audit-report-card">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => this.setState({ activeTab: key })}
            >
              <TabPane tab="Card-Wise Sales" key="cards">
                {this.renderCardSalesTab()}
              </TabPane>
              <TabPane tab="Batch-Wise Sales" key="batches">
                {this.renderBatchSalesTab()}
              </TabPane>
              <TabPane tab="Channel Distribution" key="channels">
                {this.renderChannelSplitTab()}
              </TabPane>
              <TabPane
                tab={
                  <span>
                    Margin Exceptions{" "}
                    {unresolvedCount > 0 && (
                      <Badge
                        count={unresolvedCount}
                        style={{ backgroundColor: "#DC2626", marginLeft: 6 }}
                      />
                    )}
                  </span>
                }
                key="anomalies"
              >
                {this.renderAnomaliesTab()}
              </TabPane>
            </Tabs>
          </Card>
        </Spin>

        {/* Resolution Modal */}
        {this.renderResolutionModal()}

        {/* Sold Cards Drilldown Modal */}
        {this.renderSoldCardsModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken || state.auth.token,
  loginUserData: state.auth.loginUserData,
});

export default connect(mapStateToProps)(withRouter(DailyCardAuditReport));
